import { useEffect, useState } from 'react'
import type {
  AnalysisSettings,
  GroupInput,
  MeanGroupInput,
} from './lib/stats/analysis'
import { decodeMeanState, decodeState, defaultGroupName } from './lib/urlState'
import { IssueList } from './components/IssueList'
import { SignificanceView } from './components/SignificanceView'
import { MeansView } from './components/MeansView'
import { SampleSizeView } from './components/SampleSizeView'

type Tab = 'significance' | 'means' | 'design'

const TABS: { value: Tab; label: string }[] = [
  { value: 'significance', label: '有意差検定' },
  { value: 'means', label: '連続値の指標' },
  { value: 'design', label: 'サンプルサイズ設計' },
]

const DEFAULT_SETTINGS: AnalysisSettings = {
  confidence: 0.95,
  tail: 'two-sided',
  correction: 'none',
  diagnosticPower: 0.8,
  sequential: null,
}

const DEFAULT_GROUPS: GroupInput[] = [
  { id: 'g0', name: defaultGroupName(0), visitors: 5000, conversions: 250, weight: 1 },
  { id: 'g1', name: defaultGroupName(1), visitors: 5000, conversions: 300, weight: 1 },
]

/** 客単価 1,200 円 (SD 800) と 1,260 円 (SD 850) を 5,000 人ずつ。 */
const DEFAULT_MEAN_GROUPS: MeanGroupInput[] = [
  { id: 'm0', name: defaultGroupName(0), n: 5000, mean: 1200, sd: 800 },
  { id: 'm1', name: defaultGroupName(1), n: 5000, mean: 1260, sd: 850 },
]

interface InitialState {
  tab: Tab
  groups: GroupInput[]
  meanGroups: MeanGroupInput[]
  settings: AnalysisSettings
  /** 共有された URL に読めない逐次確認の指定が入っていた */
  sequentialBroken: boolean
}

/**
 * URL に共有された状態があればそれを初期値にする。
 *
 * 比率 (g) と平均 (m) はどちらか一方しか載らないので、
 * 載っていたほうのタブを開いた状態で始める。リンクを開いた人に
 * どのタブの話なのかを探させないため。
 */
function initialState(): InitialState {
  const base: InitialState = {
    tab: 'significance',
    groups: DEFAULT_GROUPS,
    meanGroups: DEFAULT_MEAN_GROUPS,
    settings: DEFAULT_SETTINGS,
    sequentialBroken: false,
  }

  const shared = decodeState(window.location.search)
  if (shared) {
    return {
      ...base,
      sequentialBroken: shared.sequentialBroken,
      groups: shared.groups.map((group, index) => ({
        id: `g${index}`,
        ...group,
      })),
      settings: {
        ...DEFAULT_SETTINGS,
        confidence: shared.confidence,
        tail: shared.tail,
        correction: shared.correction,
        sequential: shared.sequential,
      },
    }
  }

  const sharedMeans = decodeMeanState(window.location.search)
  if (sharedMeans) {
    return {
      ...base,
      tab: 'means',
      sequentialBroken: sharedMeans.sequentialBroken,
      meanGroups: sharedMeans.groups.map((group, index) => ({
        id: `m${index}`,
        ...group,
      })),
      settings: {
        ...DEFAULT_SETTINGS,
        confidence: sharedMeans.confidence,
        tail: sharedMeans.tail,
        correction: sharedMeans.correction,
        sequential: sharedMeans.sequential,
      },
    }
  }

  return base
}

export function App() {
  const [initial] = useState(initialState)
  const [tab, setTab] = useState<Tab>(initial.tab)
  const [groups, setGroups] = useState<GroupInput[]>(initial.groups)
  const [meanGroups, setMeanGroups] = useState<MeanGroupInput[]>(
    initial.meanGroups,
  )
  const [settings, setSettings] = useState<AnalysisSettings>(initial.settings)

  // 読めない逐次確認の指定が URL にあったことは、宣言し直すまで伝え続ける。
  // 補正されているつもりで甘い判定を見せるのが、この機能で最も避けたい事故。
  const sequentialBroken = initial.sequentialBroken && settings.sequential === null

  // 比較群が 2 つ以上になったら補正を既定で有効にする。
  // 補正なしのまま複数比較するのが最も間違えやすいため、既定側で防ぐ。
  const groupCount = tab === 'means' ? meanGroups.length : groups.length
  useEffect(() => {
    if (groupCount > 2) {
      setSettings((current) =>
        current.correction === 'none'
          ? { ...current, correction: 'holm' }
          : current,
      )
    }
  }, [groupCount])

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">
          <span className="app-title-mark" aria-hidden="true">
            AB
          </span>
          ab_judge
        </h1>
        <p className="app-subtitle">
          A/B テストの結果に統計的な有意差があるかを、p 値だけでなく信頼区間で判定します。
          計算はすべてブラウザ内で行われ、入力した数値が外部に送信されることはありません。
        </p>
      </header>

      <div className="tabs" role="tablist" aria-label="機能の切り替え">
        {TABS.map((entry) => (
          <button
            key={entry.value}
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === entry.value}
            onClick={() => setTab(entry.value)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {sequentialBroken && (
        <IssueList
          issues={[
            {
              level: 'error',
              title: '共有されたリンクの逐次確認の設定を読み取れませんでした',
              detail:
                'リンクには途中経過の確認の指定が入っていましたが、値が壊れていて復元できませんでした。' +
                'いまは途中で覗いた回数を考慮しない通常の検定として判定しています。' +
                '元の実験が途中確認つきだったなら、「途中経過の確認」で回数を入れ直してください。',
            },
          ]}
        />
      )}

      {tab === 'significance' && (
        <SignificanceView
          groups={groups}
          onGroupsChange={setGroups}
          settings={settings}
          onSettingsChange={setSettings}
        />
      )}
      {tab === 'means' && (
        <MeansView
          groups={meanGroups}
          onGroupsChange={setMeanGroups}
          settings={settings}
          onSettingsChange={setSettings}
        />
      )}
      {tab === 'design' && <SampleSizeView />}

      <footer className="app-footer">
        <p>
          比率の検定は 2 標本の z 検定です。p 値は帰無仮説と整合するプールした標準誤差、
          差の信頼区間は各群を独立に推定したプールしない標準誤差で計算しています。
          両者は別の問いに答えているため、有意水準ぎわで判定と区間が食い違うことがあります。
        </p>
        <p>
          各群単体の区間は Wilson スコア区間、相対リフトの区間は対数変換した
          相対リスクの区間です。平均の検定は等分散を仮定しない Welch の t 検定で、
          自由度は Welch-Satterthwaite 近似です。多重比較は Holm 法または
          Bonferroni 法で補正し、途中経過を確認する場合は Lan-DeMets の
          アルファ消費関数で各回の有意水準を配分します。
        </p>
      </footer>
    </div>
  )
}
